import { useMemo, useCallback, useState, useEffect } from 'react';
import { MainNavbar } from '../../components/navigation/MainNavbar';
import { useTranslation } from 'react-i18next';
import { RoutePlanner } from '../../components/maps/RoutePlanner';
import { TripsMapModal } from '../../components/maps/TripsMapModal';
import { useActiveTrips } from '../../hooks/useActiveTrips';
import { TripResultCard } from '../../components/trips/TripResultCard';
import { usePublicAuth } from '../../contexts/PublicAuthContext.jsx';
import { useTripRequestMeta } from '../../hooks/useTripRequestMeta';
import { matchesSearchFilters } from '../../utils/tripSearch';
import { Link, useLocation } from 'react-router-dom';

export default function TravelerTrips() {
  const PAGE_SIZE = 12;
  const { t } = useTranslation();
  const { user, initializing } = usePublicAuth();
  const location = useLocation();
  const { carryingTrips, loading, error } = useActiveTrips('active', {
    enabled: Boolean(user),
  });
  const { completedTrips, userActiveRequests } = useTripRequestMeta(user);
  const showLoginGate = !initializing && !user;
  const queryFilters = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      from: { description: params.get('from') || '' },
      to: { description: params.get('to') || '' },
      departureDate: params.get('date') || '',
    };
  }, [location.search]);
  const [routeFilters, setRouteFilters] = useState(queryFilters);
  const [showMap, setShowMap] = useState(false);

  useEffect(() => {
    setRouteFilters(queryFilters);
  }, [queryFilters]);

  const [currentPage, setCurrentPage] = useState(1);

  const filteredCarryingTrips = useMemo(
    () =>
      carryingTrips
        .filter(trip => !completedTrips[trip.id])
        .filter(trip => matchesSearchFilters(trip, routeFilters)),
    [carryingTrips, completedTrips, routeFilters],
  );

  const mapReadyTrips = useMemo(
    () =>
      filteredCarryingTrips.filter(
        trip =>
          typeof trip.originDetails?.latitude === 'number' &&
          typeof trip.originDetails?.longitude === 'number',
      ),
    [filteredCarryingTrips],
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [routeFilters, filteredCarryingTrips.length]);

  useEffect(() => {
    const totalPages = Math.max(
      1,
      Math.ceil(filteredCarryingTrips.length / PAGE_SIZE),
    );
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [filteredCarryingTrips.length, currentPage]);

  const paginatedTrips = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredCarryingTrips.slice(start, start + PAGE_SIZE);
  }, [filteredCarryingTrips, currentPage]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredCarryingTrips.length / PAGE_SIZE),
  );

  const isOwnTrip = useCallback(
    trip => {
      if (!user) return false;
      const ownerIds = [
        trip.createdBy,
        trip.ownerId,
        trip.userId,
        trip.tripOwnerId,
      ].filter(Boolean);

      if (ownerIds.some(id => id === user.uid)) {
        return true;
      }

      const ownerEmails = [trip.createdByEmail, trip.ownerEmail].filter(
        Boolean,
      );
      return ownerEmails.some(
        email =>
          email &&
          user.email &&
          email.toLowerCase() === user.email.toLowerCase(),
      );
    },
    [user],
  );
  const handleSearch = useCallback(filters => {
    setRouteFilters(filters);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
      <MainNavbar />
      <main className="mx-auto max-w-8xl px-4 pt-28 pb-16 sm:px-6 lg:px-8">
        <RoutePlanner
          title={t('travelerTrips.title')}
          subtitle={t('travelerTrips.subtitle')}
          ctaLabel={t('travelerTrips.ctaLabel')}
          onSearchChange={handleSearch}
          initialFilters={queryFilters}
        />
        <div className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={() => setShowMap(true)}
            disabled={mapReadyTrips.length === 0}
            className={`inline-flex items-center gap-2 rounded-2xl border px-4 py-2 text-sm font-semibold transition ${
              mapReadyTrips.length === 0
                ? 'cursor-not-allowed border-gray-200 text-gray-400 dark:border-slate-800 dark:text-slate-500'
                : 'border-gray-900 text-gray-900 hover:bg-gray-100 dark:border-slate-200 dark:text-slate-100 dark:hover:bg-slate-800'
            }`}
          >
            {t('travelerTrips.showAllOnMap')}
          </button>
        </div>

        <section className="mt-10">
          <h2 className="text-xs font-semibold uppercase tracking-[0.3em] text-gray-500 dark:text-slate-400">
            {t('travelerTrips.seeActiveSenders')}
          </h2>
          <div className="mt-4 space-y-6">
            {showLoginGate ? (
              <div className="rounded-3xl bg-white dark:bg-slate-900 p-6 text-sm text-gray-600 dark:text-slate-400 shadow-lg">
                <p className="font-semibold text-gray-900 dark:text-white">
                  {t('travelerTrips.signInTitle')}
                </p>
                <p className="mt-2 text-gray-500 dark:text-slate-400">
                  {t('travelerTrips.signInDescription')}
                </p>
                <Link
                  to="/auth/login"
                  className="mt-4 inline-flex rounded-2xl bg-black dark:bg-slate-700 px-5 py-3 text-sm font-semibold text-yellow-400 transition hover:bg-gray-900 dark:hover:bg-slate-800"
                >
                  {t('incomingRequests.goToLogin')}
                </Link>
              </div>
            ) : loading ? (
              <div className="rounded-3xl bg-white dark:bg-slate-900 p-6 shadow-lg">
                <div className="h-4 w-1/3 animate-pulse rounded bg-gray-200 dark:bg-slate-800" />
                <div className="mt-6 space-y-3">
                  <div className="h-3 w-full animate-pulse rounded bg-gray-200 dark:bg-slate-800" />
                  <div className="h-3 w-11/12 animate-pulse rounded bg-gray-200 dark:bg-slate-800" />
                  <div className="h-3 w-2/3 animate-pulse rounded bg-gray-200 dark:bg-slate-800" />
                </div>
              </div>
            ) : error ? (
              <p className="rounded-3xl bg-red-50 dark:bg-red-900/20 p-4 text-sm text-red-600 dark:text-red-400">
                {error}
              </p>
            ) : filteredCarryingTrips.length === 0 ? (
              <div className="rounded-3xl bg-white dark:bg-slate-900 p-6 shadow-lg text-sm text-gray-600 dark:text-slate-400">
                <p className="font-semibold text-gray-900 dark:text-white">
                  {t('travelerTrips.emptyTitle')}
                </p>
                <p className="mt-1">
                  {t('travelerTrips.emptySubtitle')}
                </p>
                <div className="mt-4 flex flex-col gap-3 sm:flex-row">
                  <Link
                    to="/list-new-trip"
                    className="flex-1 rounded-2xl bg-black px-5 py-3 text-center text-sm font-semibold text-yellow-400 transition hover:bg-gray-900 dark:bg-yellow-500 dark:text-black dark:hover:bg-yellow-400"
                  >
                    {t('travelerTrips.listNewTrip')}
                  </Link>
                  <Link
                    to="/request-delivery"
                    className="flex-1 rounded-2xl border-2 border-gray-200 px-5 py-3 text-center text-sm font-semibold text-gray-900 transition hover:border-gray-400 dark:border-slate-700 dark:text-slate-100 dark:hover:border-slate-500"
                  >
                    {t('travelerTrips.createRequest')}
                  </Link>
                </div>
              </div>
            ) : (
              <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3">
                {paginatedTrips.map(trip => {
                  const tripIsOwn = isOwnTrip(trip);
                  const disableActions =
                    tripIsOwn || userActiveRequests[trip.id] === true;
                  const disableReason = tripIsOwn
                    ? t('travelerTrips.disableOwn')
                    : userActiveRequests[trip.id]
                    ? t('travelerTrips.disableAlreadyRequested')
                    : '';

                  return (
                    <TripResultCard
                      key={trip.id}
                      trip={trip}
                      primaryActionLabel={t('travelerTrips.primaryAction')}
                      secondaryActionLabel={t('travelerTrips.secondaryAction')}
                      disableActions={disableActions}
                      disableReason={
                        disableActions
                          ? disableReason || 'This listing is not available'
                          : ''
                      }
                    />
                  );
                })}
              </div>
            )}
            {filteredCarryingTrips.length > PAGE_SIZE && (
              <div className="mt-8 flex flex-wrap items-center justify-center gap-2">
                <button
                  type="button"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="rounded-full border px-4 py-2 text-sm font-semibold transition disabled:opacity-40"
                >
                  {t('myRequests.prev')}
                </button>
                {Array.from({ length: totalPages }, (_, index) => {
                  const page = index + 1;
                  return (
                    <button
                      key={page}
                      type="button"
                      onClick={() => setCurrentPage(page)}
                      className={`h-10 w-10 rounded-full text-sm font-semibold transition ${
                        currentPage === page
                          ? 'bg-black text-yellow-400 dark:bg-yellow-500 dark:text-black'
                          : 'border border-gray-300 text-gray-700 dark:border-slate-700 dark:text-slate-200'
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}
                <button
                  type="button"
                  onClick={() =>
                    setCurrentPage(prev => Math.min(totalPages, prev + 1))
                  }
                  disabled={currentPage === totalPages}
                  className="rounded-full border px-4 py-2 text-sm font-semibold transition disabled:opacity-40"
                >
                  {t('myRequests.next')}
                </button>
              </div>
            )}
          </div>
        </section>
      </main>
      <TripsMapModal
        visible={showMap && mapReadyTrips.length > 0}
        onClose={() => setShowMap(false)}
        trips={mapReadyTrips}
        title={t('travelerTrips.mapTitle')}
      />
    </div>
  );
}
